// ============================================================================
// crowd.js — instanced crowd renderer. ONE InstancedMesh per (unit type, team)
// → ~8 draw calls for the entire army. Limbs are animated in the VERTEX SHADER
// from per-instance `aAnim` data, so 1000 marching soldiers cost the CPU only a
// matrix write each. Reactions that need full-body motion (launch/knockback)
// are baked into the instance matrix (height + tumble) on the CPU.
//
// Instances are re-packed every frame: counters reset, active units append to
// their crowd. This avoids stale-slot bookkeeping and is O(active).
// ============================================================================

import * as THREE from 'three';
import { buildUnitGeometry } from './meshes.js';
import {
  UNIT_TYPE_COUNT, TEAM_COUNT, MAX_ENEMIES, UNIT_WARLORD,
  ST_SEEK, ST_ATTACK, ST_FLINCH, ST_LAUNCH, ST_KNOCK, ST_STUN, ST_DEAD, ST_BLOCK,
  TEAM_PLAYER,
} from '../core/constants.js';
import { lerp } from '../core/math.js';

// Brighter per-team tints than the raw faction colours so the baked grey armour
// reads well after multiplication.
const TINT = {
  0: new THREE.Color(0xb7c0cf),  // player host — cold silver
  1: new THREE.Color(0xcf6038),  // magmar — molten iron
};
const EMBER = {
  0: new THREE.Color(0x3a5c8a),  // allied seams glow faint steel-blue
  1: new THREE.Color(0xff5a12),  // magmar seams glow ember
};

// Generated armour textures per team (map-tiled onto the low-poly armour boxes).
const _armorLoader = new THREE.TextureLoader();
function armorTex(url) {
  const t = _armorLoader.load(url);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}
const ARMOR = {
  0: armorTex('art/armor_host.png'),
  1: armorTex('art/armor_magmar.png'),
};

// animState codes handed to the shader (kept distinct from logic ST_* so the
// mapping is explicit and tunable).
const A_IDLE = 0, A_WALK = 1, A_ATTACK = 2, A_FLINCH = 3, A_STUN = 6, A_DEAD = 7;

function extendMaterial(mat, emberColor) {
  mat.vertexColors = true;
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = { value: 0 };
    shader.uniforms.uEmber = { value: emberColor };
    mat.userData.shader = shader;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
        attribute float aLimb;
        attribute vec3 aPivot;
        attribute float aSeam;
        attribute vec4 aAnim;   // x=state y=animTime z=phase w=flash
        varying float vSeam;
        varying float vFlash;
        vec3 rotX(vec3 p, vec3 piv, float a){ vec3 d=p-piv; float s=sin(a),c=cos(a); return piv+vec3(d.x, c*d.y - s*d.z, s*d.y + c*d.z); }
        vec3 rotXd(vec3 d, float a){ float s=sin(a),c=cos(a); return vec3(d.x, c*d.y - s*d.z, s*d.y + c*d.z); }
      `)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        {
          float limb = aLimb;
          float st = aAnim.x, t = aAnim.y, ph = aAnim.z;
          float isWalk = (abs(st-1.0)<0.5)?1.0:0.0;
          float isAtk  = (abs(st-2.0)<0.5)?1.0:0.0;
          float wk = sin(t*9.0 + ph*6.2831);
          float ang = 0.0;
          if(limb > 2.5){                       // legs
            float dir = (limb<3.5)?1.0:-1.0;
            ang = wk*0.7*isWalk*dir;
          } else if(limb > 0.5){                 // arms
            float dir = (limb<1.5)?-1.0:1.0;
            ang = wk*0.45*isWalk*dir;
            float a = clamp(t*3.2,0.0,1.0);
            float chop = sin(a*3.14159);
            if(limb > 1.5) ang = mix(ang, -1.3 + chop*2.7, isAtk);
            else           ang = mix(ang, chop*0.4, isAtk);
          }
          float isFlinch=(abs(st-3.0)<0.5)?1.0:0.0;
          float isStun  =(abs(st-6.0)<0.5)?1.0:0.0;
          float isDead  =(abs(st-7.0)<0.5)?1.0:0.0;
          float bodyAng = isFlinch*(-0.4*exp(-t*7.0)) + isStun*(-0.3) + isDead*min(t*2.4,1.55);
          transformed = rotX(transformed, aPivot, ang);
          transformed = rotX(transformed, vec3(0.0), bodyAng);
          transformed.y += abs(wk)*0.05*isWalk;
          vSeam = aSeam;
          vFlash = aAnim.w;
          #ifdef USE_LIMBNORMAL
          #endif
        }
      `)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        {
          float limb = aLimb;
          float st = aAnim.x, t = aAnim.y, ph = aAnim.z;
          float isWalk = (abs(st-1.0)<0.5)?1.0:0.0;
          float isAtk  = (abs(st-2.0)<0.5)?1.0:0.0;
          float wk = sin(t*9.0 + ph*6.2831);
          float ang = 0.0;
          if(limb > 2.5){ float dir=(limb<3.5)?1.0:-1.0; ang = wk*0.7*isWalk*dir; }
          else if(limb > 0.5){
            float dir=(limb<1.5)?-1.0:1.0; ang = wk*0.45*isWalk*dir;
            float a=clamp(t*3.2,0.0,1.0); float chop=sin(a*3.14159);
            if(limb>1.5) ang=mix(ang,-1.3+chop*2.7,isAtk); else ang=mix(ang,chop*0.4,isAtk);
          }
          float isFlinch=(abs(st-3.0)<0.5)?1.0:0.0, isStun=(abs(st-6.0)<0.5)?1.0:0.0, isDead=(abs(st-7.0)<0.5)?1.0:0.0;
          float bodyAng=isFlinch*(-0.4*exp(-t*7.0))+isStun*(-0.3)+isDead*min(t*2.4,1.55);
          objectNormal = rotXd(objectNormal, ang);
          objectNormal = rotXd(objectNormal, bodyAng);
        }
      `);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uTime; uniform vec3 uEmber;
        varying float vSeam; varying float vFlash;`)
      .replace('#include <dithering_fragment>', `#include <dithering_fragment>
        gl_FragColor.rgb += vSeam * uEmber * (0.65 + 0.35*sin(uTime*5.0 + vSeam*9.0));
        gl_FragColor.rgb += vFlash * vec3(1.3, 0.75, 0.4);
      `);
  };
  return mat;
}

class Crowd {
  constructor(scene, type, team, capacity) {
    const geo = buildUnitGeometry(type);
    // per-instance animation data
    const anim = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    anim.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aAnim', anim);

    const mat = extendMaterial(
      new THREE.MeshLambertMaterial({ color: 0xffffff, map: ARMOR[team] }), EMBER[team]
    );
    this.mesh = new THREE.InstancedMesh(geo, mat, capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false; // crowd spans the whole field; skip cull cost
    // enable instanceColor
    this.mesh.setColorAt(0, TINT[team]);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    scene.add(this.mesh);

    this.anim = anim;
    this.tint = TINT[team];
    this.material = mat;
    this.count = 0;
    this.capacity = capacity;
  }
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _xAxis = new THREE.Vector3(1, 0, 0);

export class CrowdRenderer {
  constructor(scene) {
    this.crowds = [];  // indexed [type*TEAM_COUNT + team]
    for (let type = 0; type < UNIT_TYPE_COUNT; type++) {
      for (let team = 0; team < TEAM_COUNT; team++) {
        // Sized to the whole enemy pool so a crowd can never overflow.
        this.crowds[type * TEAM_COUNT + team] = new Crowd(scene, type, team, MAX_ENEMIES);
      }
    }
  }

  // Recolour the Magmar-team (enemy) crowds so different invading factions read
  // distinctly (frost = pale blue, carrion = bog green, magmar = molten iron).
  // ember is the seam-glow colour (applied to the shader uniform once compiled).
  setEnemyTint(tintHex, emberHex) {
    for (let type = 0; type < UNIT_TYPE_COUNT; type++) {
      const c = this.crowds[type * TEAM_COUNT + 1]; // team 1 = TEAM_MAGMAR
      if (!c) continue;
      c.tint.setHex(tintHex);
      c._emberHex = (emberHex != null) ? emberHex : null;
      const sh = c.material.userData.shader;
      if (sh && emberHex != null) { sh.uniforms.uEmber.value.setHex(emberHex); c._emberHex = null; }
    }
  }

  _animStateFor(st) {
    switch (st) {
      case ST_SEEK: return A_WALK;
      case ST_ATTACK: return A_ATTACK;
      case ST_FLINCH: return A_FLINCH;
      case ST_STUN: return A_STUN;
      case ST_DEAD: return A_DEAD;
      case ST_BLOCK: return A_IDLE;
      default: return A_IDLE;
    }
  }

  update(world, alpha, timeSec) {
    for (let i = 0; i < this.crowds.length; i++) this.crowds[i].count = 0;

    const units = world.units.active;
    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      if (u.type === UNIT_WARLORD) continue; // named officers are drawn by WarlordRenderer
      const crowd = this.crowds[u.type * TEAM_COUNT + u.team];
      const idx = crowd.count;
      if (idx >= crowd.capacity) continue;

      // interpolated ground position
      const x = lerp(u.px, u.x, alpha);
      const y = lerp(u.py, u.y, alpha);
      const z = lerp(u.pz, u.z, alpha);

      // build instance matrix: Y-rotation (facing) + optional tumble (launch/knock)
      _e.set(0, u.heading, 0);
      _q.setFromEuler(_e);
      // Living launch/knock ragdolls tumble via the matrix; corpses topple via
      // the shader (isDead bodyAng), so ST_DEAD is intentionally excluded here.
      if (u.tumble !== 0 && (u.state === ST_LAUNCH || u.state === ST_KNOCK)) {
        _q.multiply(_qTumble(u.tumble));
      }
      _p.set(x, y, z);
      _s.setScalar(u.scale);
      _m.compose(_p, _q, _s);
      crowd.mesh.setMatrixAt(idx, _m);
      crowd.mesh.setColorAt(idx, crowd.tint);

      // per-instance anim data
      const a = crowd.anim.array;
      const st = this._animStateFor(u.state);
      const o = idx * 4;
      a[o] = st;
      a[o + 1] = (st === A_ATTACK || st === A_FLINCH || st === A_DEAD) ? u.stateT : timeSec;
      a[o + 2] = u.animPhase;
      a[o + 3] = u.flash;

      crowd.count = idx + 1;
    }

    // flush GPU buffers
    for (let i = 0; i < this.crowds.length; i++) {
      const c = this.crowds[i];
      c.mesh.count = c.count;
      c.mesh.instanceMatrix.needsUpdate = true;
      if (c.mesh.instanceColor) c.mesh.instanceColor.needsUpdate = true;
      c.anim.needsUpdate = true;
      const sh = c.material.userData.shader;
      if (sh) {
        sh.uniforms.uTime.value = timeSec;
        // apply a deferred faction ember colour once the shader has compiled
        if (c._emberHex != null) { sh.uniforms.uEmber.value.setHex(c._emberHex); c._emberHex = null; }
      }
    }
  }

  get activeDrawCalls() {
    let n = 0;
    for (let i = 0; i < this.crowds.length; i++) if (this.crowds[i].count > 0) n++;
    return n;
  }
}

// scratch tumble quaternion (rotate about the world side axis for a tumble)
const _qt = new THREE.Quaternion();
function _qTumble(angle) {
  return _qt.setFromAxisAngle(_xAxis, angle);
}
